import { RotateCcw } from "lucide-react";

// Step 4: gas engine / diesel / OCGT / CCGT are priced ONLY by $/kW-by-unit-size
// curves (size_curves). One field per curve point; HFO keeps one flat rate.
export const THERMAL_CURVE_TECHS = [
  ["gas_engine", "Gas engine"],
  ["diesel", "Diesel genset"],
  ["ocgt", "OCGT"],
  ["ccgt", "CCGT"],
];
export const THERMAL_FLAT_FIELDS = [["liquid_fuel_units", "hfo", "HFO CAPEX ($/kW)"]];

const parse = (raw) => {
  try { return JSON.parse(raw && raw.trim() ? raw : "{}"); } catch { return null; }
};
const dump = (o) => JSON.stringify(o, null, 2);
const prune = (o, sec, tech) => {
  if (o[sec]?.[tech] && !Object.keys(o[sec][tech]).length) delete o[sec][tech];
  if (o[sec] && !Object.keys(o[sec]).length) delete o[sec];
};

export const setThermalRate = (raw, sec, tech, value) => {
  const o = parse(raw);
  if (!o) return raw;
  if (value === undefined) {
    if (o[sec]?.[tech]) delete o[sec][tech].capex_per_kw;
    prune(o, sec, tech);
  } else {
    o[sec] = { ...(o[sec] || {}) };
    o[sec][tech] = { ...(o[sec][tech] || {}), capex_per_kw: value };
  }
  return dump(o);
};

// Writes the whole points list for one tech (the merge replaces it wholesale).
export const setCurvePoint = (raw, tech, defPoints, idx, value) => {
  const o = parse(raw);
  if (!o) return raw;
  if (idx === undefined) {
    if (o.size_curves?.[tech]) delete o.size_curves[tech].points;
    prune(o, "size_curves", tech);
    return dump(o);
  }
  const pts = (o.size_curves?.[tech]?.points || defPoints).map((p) => [...p]);
  pts[idx][1] = value;
  o.size_curves = { ...(o.size_curves || {}) };
  o.size_curves[tech] = { ...(o.size_curves[tech] || {}), points: pts };
  return dump(o);
};

// Step 5a: efficiency / fuel use / part load / fuel EFs — one field per value.
const getPath = (o, path) => path.reduce((x, k) => (x == null ? undefined : x[k]), o);
export const setPath = (raw, path, value) => {
  const o = parse(raw);
  if (!o) return raw;
  let x = o;
  path.slice(0, -1).forEach((k) => { x[k] = { ...(x[k] || {}) }; x = x[k]; });
  if (value === undefined) delete x[path[path.length - 1]];
  else x[path[path.length - 1]] = value;
  return dump(o);
};
export const EFFICIENCY_CURVES = [
  [["unit_efficiency", "gas_engine", "points"], "Gas engine efficiency (LHV) by unit size", "eff-gas_engine"],
  [["diesel_l_per_kwh", "points"], "Diesel fuel use (l/kWh) by unit size", "sfc-diesel"],
  // STEP 5b — per-unit costs of diesel and gas-engine banks, by unit size.
  [["per_unit_costs", "adder_usd", "points"], "Per-unit CAPEX adder ($/unit) by unit size", "per-unit-adder"],
  [["per_unit_costs", "fixed_om_usd_yr", "points"], "Per-unit fixed O&M ($/unit/yr) by unit size", "per-unit-fixed-om"],
];
export const EFFICIENCY_SCALARS = [
  [["part_load", "cf_threshold"], "Part-load threshold (annual loading)", "part-load-threshold"],
  [["part_load", "eff_factor"], "Part-load efficiency factor", "part-load-factor"],
  [["fuel_emission_factors", "natural_gas_kg_per_gj"], "Natural gas CO2 (kg/GJ)", "ef-gas"],
  [["fuel_emission_factors", "diesel_kg_per_l"], "Diesel CO2 (kg/l)", "ef-diesel"],
  // STEP 5b — variable O&M per kWh generated, one rate per technology.
  [["variable_om_usd_per_kwh", "diesel"], "Diesel variable O&M ($/kWh)", "var-om-diesel"],
  [["variable_om_usd_per_kwh", "gas_engine"], "Gas engine variable O&M ($/kWh)", "var-om-gas-engine"],
  [["variable_om_usd_per_kwh", "ocgt"], "OCGT variable O&M ($/kWh)", "var-om-ocgt"],
  [["variable_om_usd_per_kwh", "ccgt"], "CCGT variable O&M ($/kWh)", "var-om-ccgt"],
  // STEP 6 — overhauls: interval in run-hours per unit (EOH for turbines) and cost as a
  // fraction of the bank's equipment CAPEX at its unit size (x country factor only).
  [["overhauls", "gas_engine", "top_end_interval_h"], "Gas engine top-end overhaul interval (h)", "ovh-gas-engine-top-end-h"],
  [["overhauls", "gas_engine", "top_end_pct"], "Gas engine top-end overhaul cost (fraction of equipment CAPEX)", "ovh-gas-engine-top-end-pct"],
  [["overhauls", "gas_engine", "major_interval_h"], "Gas engine major overhaul interval (h)", "ovh-gas-engine-major-h"],
  [["overhauls", "gas_engine", "major_pct"], "Gas engine major overhaul cost (fraction of equipment CAPEX)", "ovh-gas-engine-major-pct"],
  [["overhauls", "diesel", "major_interval_h"], "Diesel major overhaul interval (h)", "ovh-diesel-major-h"],
  [["overhauls", "diesel", "major_pct"], "Diesel major overhaul cost (fraction of equipment CAPEX)", "ovh-diesel-major-pct"],
  [["overhauls", "ocgt", "hot_gas_path_interval_h"], "OCGT hot-gas-path interval (EOH)", "ovh-ocgt-hgp-h"],
  [["overhauls", "ocgt", "hot_gas_path_pct"], "OCGT hot-gas-path cost (fraction of equipment CAPEX)", "ovh-ocgt-hgp-pct"],
  [["overhauls", "ocgt", "major_interval_h"], "OCGT major overhaul interval (EOH)", "ovh-ocgt-major-h"],
  [["overhauls", "ocgt", "major_pct"], "OCGT major overhaul cost (fraction of equipment CAPEX)", "ovh-ocgt-major-pct"],
  [["overhauls", "ccgt", "hot_gas_path_interval_h"], "CCGT hot-gas-path interval (EOH)", "ovh-ccgt-hgp-h"],
  [["overhauls", "ccgt", "hot_gas_path_pct"], "CCGT hot-gas-path cost (fraction of equipment CAPEX)", "ovh-ccgt-hgp-pct"],
  [["overhauls", "ccgt", "major_interval_h"], "CCGT major overhaul interval (EOH)", "ovh-ccgt-major-h"],
  [["overhauls", "ccgt", "major_pct"], "CCGT major overhaul cost (fraction of equipment CAPEX)", "ovh-ccgt-major-pct"],
];

const EfficiencyFields = ({ defaults, raw, onChange, locked }) => {
  const ov = parse(raw) || {};
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-testid="admin-efficiency-fields">
      {EFFICIENCY_CURVES.map(([path, label, id]) => {
        const def = getPath(defaults, path) || [];
        const set = getPath(ov, path);
        return (
          <div key={id} className="p-4 rounded-md" style={{ background: "var(--paper)", border: "1px solid var(--hairline)" }}>
            <div className="label-field">{label}</div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {def.map(([kw], i) => (
                <label key={kw} className="text-xs font-mono flex items-center gap-2" style={{ color: "var(--muted)" }}>
                  <span className="w-16 shrink-0">{fmtKw(kw)}</span>
                  <input type="number" step={0.001} className="field-input font-mono !py-1" disabled={locked}
                    value={(set || def)[i][1]} data-testid={`admin-${id}-${kw}`}
                    onChange={(e) => {
                      const pts = (set || def).map((p) => [...p]);
                      pts[i][1] = parseFloat(e.target.value) || 0;
                      onChange(setPath(raw, path, pts));
                    }} />
                </label>
              ))}
            </div>
          </div>
        );
      })}
      {EFFICIENCY_SCALARS.map(([path, label, id]) => (
        <label key={id} className="p-4 rounded-md block" style={{ background: "var(--paper)", border: "1px solid var(--hairline)" }}>
          <span className="label-field">{label}</span>
          <input type="number" step={0.01} className="field-input font-mono" disabled={locked}
            value={getPath(ov, path) ?? getPath(defaults, path) ?? ""} data-testid={`admin-${id}`}
            onChange={(e) => onChange(setPath(raw, path, parseFloat(e.target.value) || 0))} />
        </label>
      ))}
    </div>
  );
};

const fmtKw = (kw) => (kw >= 1000 ? `${kw / 1000} MW` : `${kw} kW`);

const CurveCard = ({ tech, label, def, set, raw, onChange, locked }) => (
  <div className="p-4 rounded-md" style={{ background: "var(--paper)", border: set ? "1px solid var(--brand-green)" : "1px solid var(--hairline)" }}
    data-testid={`admin-thermal-curve-${tech}`}>
    <div className="label-field flex items-center justify-between">
      <span>{label} CAPEX ($/kW by unit size)</span>
      {set && (
        <button onClick={() => onChange(setCurvePoint(raw, tech, def.points))} className="btn-ghost !p-0 text-xs"
          data-testid={`revert-thermal-curve-${tech}`}>
          <RotateCcw size={11} className="inline" /> revert
        </button>
      )}
    </div>
    <div className="grid grid-cols-2 gap-2 mt-2">
      {def.points.map(([kw, dv], i) => (
        <label key={kw} className="text-xs font-mono flex items-center gap-2" style={{ color: "var(--muted)" }}>
          <span className="w-16 shrink-0">{fmtKw(kw)}</span>
          <input type="number" step={1} className="field-input font-mono !py-1" disabled={locked}
            value={(set || def.points)[i][1]}
            onChange={(e) => onChange(setCurvePoint(raw, tech, def.points, i, parseFloat(e.target.value) || 0))}
            title={`default ${dv}`} data-testid={`admin-thermal-curve-${tech}-${kw}`} />
        </label>
      ))}
    </div>
    <div className="text-xs mt-2 font-mono" style={{ color: "var(--muted)" }}>
      reference size {fmtKw(def.reference_kw)} (only where no unit size exists)
    </div>
  </div>
);

export const ThermalRates = ({ defaults, raw, onChange, disabled }) => {
  const ov = parse(raw);
  const locked = disabled || !ov;
  return (
    <div className="card p-6 md:p-8 mb-6" data-testid="admin-thermal-rates">
      <div className="overline mb-3">Thermal CAPEX</div>
      <h2 className="font-display text-xl mb-2">Thermal $/kW — one field per curve point</h2>
      <p className="text-sm mb-4" style={{ color: "var(--muted)" }}>
        Equipment row (landed and erected unit), before BoP/EPC, contingency and the country multiplier.
        Interpolated in log(unit size), flat beyond the end points. Saved into the thermal overrides below.
      </p>
      {!ov && <div className="text-xs mb-3" style={{ color: "#873F00" }} data-testid="admin-thermal-rates-locked">Fix the thermal JSON to edit these fields.</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {THERMAL_CURVE_TECHS.map(([tech, label]) => {
          const def = defaults?.size_curves?.[tech];
          if (!def) return null;
          return <CurveCard key={tech} tech={tech} label={label} def={def} set={ov?.size_curves?.[tech]?.points}
            raw={raw} onChange={onChange} locked={locked} />;
        })}
        {THERMAL_FLAT_FIELDS.map(([sec, tech, label]) => {
          const defV = defaults?.[sec]?.[tech]?.capex_per_kw;
          const set = ov?.[sec]?.[tech]?.capex_per_kw;
          return (
            <div key={tech} className="p-4 rounded-md" style={{ background: "var(--paper)", border: set !== undefined ? "1px solid var(--brand-green)" : "1px solid var(--hairline)" }}>
              <label className="label-field flex items-center justify-between">
                <span>{label}</span>
                {set !== undefined && (
                  <button onClick={() => onChange(setThermalRate(raw, sec, tech, undefined))} className="btn-ghost !p-0 text-xs" data-testid={`revert-thermal-${tech}`}>
                    <RotateCcw size={11} className="inline" /> revert
                  </button>
                )}
              </label>
              <input type="number" step={1} className="field-input font-mono" value={set ?? defV ?? ""} disabled={locked}
                onChange={(e) => onChange(setThermalRate(raw, sec, tech, parseFloat(e.target.value) || 0))}
                data-testid={`admin-thermal-rate-${tech}`} />
              <div className="text-xs mt-1 font-mono" style={{ color: "var(--muted)" }}>default {defV}</div>
            </div>
          );
        })}
      </div>
      <EfficiencyFields defaults={defaults} raw={raw} onChange={onChange} locked={locked} />
    </div>
  );
};
